/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { connect } from 'react-redux';
import Papa, { ParseResult } from 'papaparse';
import { Tooltip, Icon, Tumbler, CollapsibleBlock } from 'factor';

import { blockStyles } from 'components/Block';
import { UINoteIcon } from 'components/UINoteIcon';
import { Option } from 'models/Option';
import { advancedTargetingActions, Data, ExchangeData } from 'store/advancedTargeting/actions';
import { AppState } from 'models/Store';
import { ExistingCampaignData } from 'models/ExistingCampaign';
import { AdvancedTargetingOptionKeys, filterLabels } from 'store/advancedTargeting/constants';
import { AdvancedTargetingState } from 'store/advancedTargeting/reducer';
import { CampaignInfoField } from 'models/CampaignInfoFields';
import { applicationActions } from 'store/app/actions';
import { ErrorCreatingResponse } from 'models/Response';
import { errorFieldsMapper } from 'constants/errorFieldsMapper';
import {
  formatAdvancedTargetingFields,
  groupMultiselectedValues,
  getTargetingTooltip,
} from 'utils/helpers';
import { ALL_EXPANDED, CollapseMode } from 'store/app/constants';

import { SelectExchangeWrapper } from './SelectExchangeWrapper';
import { FilterList } from './FilterList';

interface Props {
  editableCampaign: ExistingCampaignData | null;
  advancedTargeting: AdvancedTargetingState;
  selectedExchanges: Option<number>[];
  errorCreating: ErrorCreatingResponse | null;
  selectAdvancedTargetingField: (data: Data) => void;
  setExchanges: (data: ExchangeData) => void;
  selectExchanges: (data: Option<number>[]) => void;
  resetError: () => void;
  isCollapseOpen?: boolean;
  onToggle?: (isOpened: boolean) => void;
  collapseMode?: CollapseMode;
}

interface State {
  whitelist: boolean;
  blacklist: boolean;
  filterKeys: AdvancedTargetingOptionKeys[];
}

class AdvancedTargetingBlockComponent extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      whitelist: false,
      blacklist: false,
      filterKeys: Object.keys(filterLabels) as AdvancedTargetingOptionKeys[],
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { editableCampaign, advancedTargeting, selectExchanges } = this.props;
    const { exchanges } = advancedTargeting;
    const { filterKeys } = this.state;

    if (editableCampaign) {
      for (let i = 0; i < filterKeys.length; i += 1) {
        const key = filterKeys[i];
        if (editableCampaign[key] && editableCampaign !== prevProps.editableCampaign) {
          this.handleChangeInStore(key)(String(editableCampaign[key]));
          this.enableList(key);
        }
      }

      if (
        editableCampaign.exchanges &&
        (editableCampaign !== prevProps.editableCampaign ||
          exchanges.length !== prevProps.advancedTargeting.exchanges.length)
      ) {
        const exchangeIds = editableCampaign.exchanges.split(',').map((i) => +i);
        this.handleSelectExchanges(exchanges.filter((o) => exchangeIds.includes(o.value)));
      }
    } else if (exchanges.length !== prevProps.advancedTargeting.exchanges.length) {
      selectExchanges(exchanges);
    }
  }

  enableList = (key: string) => {
    const { whitelist, blacklist } = this.state;
    if (key.startsWith('black') && !blacklist) {
      this.setState({
        blacklist: true,
      });
    } else if (key.startsWith('white') && !whitelist) {
      this.setState({
        whitelist: true,
      });
    }
  };

  handleChangeInStore = (field: AdvancedTargetingOptionKeys) => (value: string) => {
    const { selectAdvancedTargetingField, resetError, errorCreating } = this.props;
    selectAdvancedTargetingField({
      key: CampaignInfoField[field],
      value,
    });
    if (errorCreating && errorCreating.errorField === errorFieldsMapper[field]) {
      resetError();
    }
  };

  fileUploadCSVHandler = (file: FileList, cb: (data: string[][]) => void) => {
    Papa.parse(file[0], {
      complete: (results: ParseResult<string[]>) => {
        cb(results.data);
      },
    });
  };

  handleCSVUpload = (field: AdvancedTargetingOptionKeys) => (files: FileList) => {
    const fileExtension = files[0].name.split('.').splice(-1)[0];
    if (fileExtension === 'csv') {
      this.fileUploadCSVHandler(files, (data: string[][]) => {
        this.handleChangeInStore(field)(data.flat().join());
      });
    }
  };

  handleSelectExchanges = (value: Option<number>[]) => {
    const { selectExchanges } = this.props;
    selectExchanges(value);
  };

  handleSetExchanges = (value: Option<number>[]) => {
    const { setExchanges } = this.props;
    setExchanges({
      key: 'exchanges',
      value,
    });
  };

  getKeyIcon = (targeting: 'targeted' | 'blocked', facet: AdvancedTargetingOptionKeys) => (
    <UINoteIcon inline text={getTargetingTooltip(targeting, facet)} marginLeft={0} />
  );

  getHeaderObj = () => {
    const { advancedTargeting } = this.props;

    return {
      title: (
        <>
          <Icon name="Advanced" className="block_title-icon" />
          Advanced
        </>
      ),
      summary: {
        'Device IDs Allowlist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.whiteListedDeviceId],
        ),
        'Device IDs Blocklist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.blackListedDeviceId],
        ),
        'IPs Allowlist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.whiteListedIp],
        ),
        'IPs Blocklist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.blackListedIp],
        ),
        'Android Package Names Allowlist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.whiteListedPackageName],
        ),
        'Android Package Names Blocklist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.blackListedPackageName],
        ),
        'iOS Apps IDs Allowlist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.whiteListedAppId],
        ),
        'iOS Apps IDs Blocklist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.blackListedAppId],
        ),
        'Site Domains Allowlist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.whiteListedSiteDomain],
        ),
        'Site Domains Blocklist': formatAdvancedTargetingFields(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.blackListedSiteDomain],
        ),
        Exchanges: groupMultiselectedValues(
          advancedTargeting.sidebarCampaignInfo[CampaignInfoField.exchanges],
          advancedTargeting.exchanges.length,
        ),
      },
      keyIcon: {
        'Device IDs Allowlist': this.getKeyIcon('targeted', 'whiteListedDeviceId'),
        'Device IDs Blocklist': this.getKeyIcon('blocked', 'blackListedDeviceId'),
        'IPs Allowlist': this.getKeyIcon('targeted', 'whiteListedIp'),
        'IPs Blocklist': this.getKeyIcon('blocked', 'blackListedIp'),
        'Android Package Names Allowlist': this.getKeyIcon('targeted', 'whiteListedPackageName'),
        'Android Package Names Blocklist': this.getKeyIcon('blocked', 'blackListedPackageName'),
        'iOS Apps IDs Allowlist': this.getKeyIcon('targeted', 'whiteListedAppId'),
        'iOS Apps IDs Blocklist': this.getKeyIcon('blocked', 'blackListedAppId'),
        'Site Domains Allowlist': this.getKeyIcon('targeted', 'whiteListedSiteDomain'),
        'Site Domains Blocklist': this.getKeyIcon('blocked', 'blackListedSiteDomain'),
      },
    };
  };

  resetStoreItems = (isWhitelist: boolean) => {
    this.handleChangeInStore(isWhitelist ? 'whiteListedAppId' : 'blackListedAppId')('');
    this.handleChangeInStore(isWhitelist ? 'whiteListedDeviceId' : 'blackListedDeviceId')('');
    this.handleChangeInStore(isWhitelist ? 'whiteListedIp' : 'blackListedIp')('');
    this.handleChangeInStore(isWhitelist ? 'whiteListedPackageName' : 'blackListedPackageName')('');
    this.handleChangeInStore(isWhitelist ? 'whiteListedSiteDomain' : 'blackListedSiteDomain')('');
  };

  render() {
    const { whitelist, blacklist } = this.state;
    const { selectedExchanges, isCollapseOpen, onToggle, collapseMode } = this.props;

    return (
      <CollapsibleBlock
        isCollapseOpen={isCollapseOpen}
        onToggle={onToggle}
        collapsible={collapseMode !== ALL_EXPANDED}
        header={this.getHeaderObj()}
        customStyle={{
          block: `${blockStyles.block} ${!isCollapseOpen ? blockStyles.collapsed : ''}`,
          panelHeaderExpand: blockStyles.panelHeaderExpand,
          panelHeaderCollapse: blockStyles.panelHeaderCollapse,
        }}
      >
        <div data-qa="161" className={`${blockStyles.blockRow}`}>
          <div data-qa="162" className={`${blockStyles.input} ${blockStyles.first}`}>
            <label data-qa="163" className={`text-label ${blockStyles.inputLabel}`}>
              <span data-qa="164">Exchanges</span>
              <Tooltip label="Exchanges">
                <Icon className="p-1 ml-2" name="Question" />
              </Tooltip>
            </label>

            <div data-qa="165" className="col-3 pl-0">
              <SelectExchangeWrapper
                label="Exchanges"
                placeholder="Select Exchanges"
                value={selectedExchanges}
                onChange={this.handleSelectExchanges}
                fetchedData={this.handleSetExchanges}
              />
            </div>
          </div>
        </div>

        <div data-qa="166" className={`row ${blockStyles.blockRow}`}>
          <div data-qa="167" className={`col-4 ${blockStyles.input} ${blockStyles.first}`}>
            <label data-qa="168" className={`text-label ${blockStyles.inputLabel}`}>
              <span data-qa="169">Allowlist</span>
              <Tooltip label="Allowlist">
                <Icon className="p-1 ml-2" name="Question" />
              </Tooltip>
            </label>
            <Tumbler
              name="whitelist"
              id="whitelist"
              on={whitelist}
              onChange={() => {
                if (whitelist) {
                  this.resetStoreItems(true);
                }
                this.setState({
                  whitelist: !whitelist,
                });
              }}
            />
          </div>
        </div>

        {whitelist && (
          <>
            <FilterList
              filters={['whiteListedDeviceId', 'whiteListedIp']}
              handleChange={this.handleChangeInStore}
              handleCSVUpload={this.handleCSVUpload}
              targetingType="targeted"
            />
            <FilterList
              filters={['whiteListedPackageName', 'whiteListedAppId']}
              handleChange={this.handleChangeInStore}
              handleCSVUpload={this.handleCSVUpload}
              targetingType="targeted"
            />
            <FilterList
              filters={['whiteListedSiteDomain']}
              handleChange={this.handleChangeInStore}
              handleCSVUpload={this.handleCSVUpload}
              targetingType="targeted"
            />
          </>
        )}

        <div data-qa="170" className={`row ${blockStyles.blockRow}`}>
          <div data-qa="171" className={`col-4 ${blockStyles.input} ${blockStyles.first}`}>
            <label data-qa="172" className={`text-label ${blockStyles.inputLabel}`}>
              <span data-qa="173">Blocklist</span>
              <Tooltip label="Blocklist">
                <Icon className="p-1 ml-2" name="Question" />
              </Tooltip>
            </label>
            <Tumbler
              name="blacklist"
              id="blacklist"
              on={blacklist}
              onChange={() => {
                if (blacklist) {
                  this.resetStoreItems(false);
                }
                this.setState({
                  blacklist: !blacklist,
                });
              }}
            />
          </div>
        </div>

        {blacklist && (
          <>
            <FilterList
              filters={['blackListedDeviceId', 'blackListedIp']}
              handleChange={this.handleChangeInStore}
              handleCSVUpload={this.handleCSVUpload}
              targetingType="blocked"
            />
            <FilterList
              filters={['blackListedPackageName', 'blackListedAppId']}
              handleChange={this.handleChangeInStore}
              handleCSVUpload={this.handleCSVUpload}
              targetingType="blocked"
            />
            <FilterList
              filters={['blackListedSiteDomain']}
              handleChange={this.handleChangeInStore}
              handleCSVUpload={this.handleCSVUpload}
              targetingType="blocked"
            />
          </>
        )}
      </CollapsibleBlock>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  editableCampaign: state.app.editableCampaign,
  advancedTargeting: state.advancedTargeting,
  errorCreating: state.app.errorCreating,
  selectedExchanges: state.advancedTargeting.sidebarCampaignInfo[CampaignInfoField.exchanges],
});

const mapActions = {
  selectAdvancedTargetingField: advancedTargetingActions.selectAdvancedTargetingField,
  selectExchanges: advancedTargetingActions.selectExchanges,
  setExchanges: advancedTargetingActions.setExchanges,
  resetError: applicationActions.resetError,
};

export const AdvancedTargetingBlock = connect(
  mapStateToProps,
  mapActions,
)(AdvancedTargetingBlockComponent);
